import { Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, OnInit, signal, untracked, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { addIcons } from 'ionicons';

import { Storage } from '@ionic/storage-angular';

import { FormsModule } from '@angular/forms';

import {
  IonApp,
  IonRouterOutlet,
  MenuController,
  ToastController,
  IonNav,
  ModalController,
} from '@ionic/angular/standalone';
import {
  calendarOutline,
  hammer,
  help,
  informationCircleOutline,
  logIn,
  logOut,
  mapOutline,
  moonOutline,
  peopleOutline,
  person,
  personAdd,
  close,
} from 'ionicons/icons';

import { AuthService } from './providers/auth.service';
import { ErrorService } from './providers/error.service';
import { SettingsService } from './providers/settings.service';

import { LoginPage } from './pages/login/login';
import { PushNotificationsService } from './providers/push-notifications.service';
import { PwaInstallService } from './providers/pwa-install.service';
import { filter } from 'rxjs';
import { IosInstallModalComponent } from './components/ios-install-modal/ios-install-modal.component';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        IonRouterOutlet,
        IonApp,
        FormsModule,
        IonNav,
    ],
    providers: [MenuController, ToastController],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit {
  private settingsService = inject(SettingsService);
  private router = inject(Router);
  private storage = inject(Storage);
  private modalCtrl = inject(ModalController);
  
  authService = inject(AuthService);
  private swUpdate = inject(SwUpdate);
  private toastCtrl = inject(ToastController);
  private menu = inject(MenuController);
  private errorService = inject(ErrorService);
  private pushNotificationsService = inject(PushNotificationsService);
  private pwaInstallService = inject(PwaInstallService);
  private appEntryRoute;

  isPushNotificationToastOpen = signal<boolean>(false);
  isPushNotificationPermissionAlertOpen = signal<boolean>(false);
  canInstallAndroid = this.pwaInstallService.canInstallAndroid;

  loginComponent = LoginPage;
  
  dark = false;

  constructor() {
    this.pwaInstallService.init();
    this.appEntryRoute = window.location.pathname + window.location.search;
 
    addIcons({
      calendarOutline,
      peopleOutline,
      mapOutline,
      informationCircleOutline,
      person,
      help,
      logOut,
      logIn,
      personAdd,
      moonOutline,
      hammer,
      close,
    });

    effect(() => {
      const tick = this.errorService.errorTick();
      if (tick === 0) return;
      this._showNextError();
    });

    effect(() => {
      const tick = this.pushNotificationsService.notificationTick();
      if (tick === 0) return;
      this._showNextPushNotification();
    });

    effect(async () => {
      if (this.authService.isLoggedIn()) {
        // if app entry route starts with /app, don't navigate to start route
        if (this.appEntryRoute && this.appEntryRoute.startsWith('/app')) {
          this.router.navigate([this.appEntryRoute]);
          this.appEntryRoute = null;
        } else {
          await this.authService.navigateToStartRoute();
        }
      }
    });

    effect(() => {
      if (this.pwaInstallService.showInstallIos()) {
        this._showIosInstallModal();
      }
    });

    effect(async () => {
      if (this.authService.isLoggedIn()) {
        const privilegeAlertShown = untracked(() => this.pushNotificationsService.privilegeAlertShown());
        if (!privilegeAlertShown) {
         this.isPushNotificationPermissionAlertOpen.set(true);
        } else {
          await this.pushNotificationsService.requestNotificationPermission();
        }
      }
    });
  }
  
  async ngOnInit() {
    // Handle navigation from push notifications
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'NAVIGATE') {
        this.router.navigateByUrl(event.data.url);
      }
    });

    await this.storage.create();
    await this.settingsService.init();
    await this.pushNotificationsService.init();

    this.swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(async () => {
        const toast = await this.toastCtrl.create({
          message: 'Nova verzija dostupna!',
          position: 'middle',
          buttons: [
            {
              text: 'Ažuriraj',
              handler: () => {
                this.swUpdate.activateUpdate()
                  .then(() => window.location.reload());
              }
            },
            {
              text: 'Kasnije',
              role: 'cancel'
            }
          ]
        });

      await toast.present();
    });

    this.swUpdate.unrecoverable.subscribe(async () => {
      const toast = await this.toastCtrl.create({
        message: 'Aplikacija je u neispravnom stanju. Molimo osvježite stranicu.',
        position: 'bottom',
        buttons: [{
          text: 'Osvježi',
          handler: () => window.location.reload()
        }]
      });
      await toast.present();
    });

    document.addEventListener('visibilitychange', async() => {
      if (!this.swUpdate.isEnabled) {
        // TODO: handle this better or fix it 
        const toast = await this.toastCtrl.create({
          message: 'Service worker nije aktivan!',
          position: 'middle',
          buttons: [{
            text: 'Zatvori',
            handler: () => toast.dismiss()
          }]
        });
        await toast.present();
      }
      if (document.visibilityState === 'visible' && this.swUpdate.isEnabled) {
        this.swUpdate.checkForUpdate();
      }
    });

    // Check authentication status and redirect if needed
    await this.authService.checkLoginStatus();
  }

  openTutorial() {
    this.menu.enable(false);
    this.storage.set('ion_did_tutorial', false);
    this.router.navigateByUrl('/tutorial');
  }

  toggleDarkMode() {
    document.documentElement.classList.toggle('ion-palette-dark', this.dark);
  }

  async onPushNotificationPermissionAlertDismiss() {
    this.isPushNotificationPermissionAlertOpen.set(false);
    await this.pushNotificationsService.requestNotificationPermission();
  }

  async onInstallAndroid() {
    await this.pwaInstallService.requestInstall();
  }

  private async _showNextError() {
    const err = this.errorService.dequeue();
    if (!err) return;
    const toast = await this.toastCtrl.create({
      message: err.message,
      duration: 3000,
      color: 'danger',
      position: 'middle',
    });
    await toast.present();
  }
  
  private async _showNextPushNotification() {
    const payload = this.pushNotificationsService.dequeue();
    if (!payload) return;
    const { title, body, url } = payload.data ?? {};

    const toast = await this.toastCtrl.create({
      header: title,
      message: body,
      duration: 1000 *  4000,
      cssClass: 'push-notification-toast',
      position: 'top',
      buttons: [
        ...(url ? [{
          text: 'Otvori',
          handler: () => {
            this.router.navigateByUrl(url);
            toast.dismiss();
          }
        }] : []),
        { icon: 'close', role: 'cancel' }
      ]
    });
    await toast.present();
  }

  async _showIosInstallModal() {
    const modal = await this.modalCtrl.create({
      component: IosInstallModalComponent,
      breakpoints: [0, 0.5],
      initialBreakpoint: 0.5,
      handleBehavior: 'cycle'
    });
    await modal.present();
  }
}
